import { useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import SEO from '../components/SEO'
import { blogPosts, categories } from '../data/blogData'
import { Helmet } from 'react-helmet-async'

const BlogPost = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  
  const post = useMemo(() => blogPosts.find((p) => p.slug === slug), [slug])
  const category = useMemo(() => post ? categories.find((c) => c.id === post.category) : null, [post])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!post) {
      navigate('/blog')
    }
  }, [post, navigate])

  if (!post) return null

  // Generate Article JSON-LD Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "MindVista",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mindvista.io/images/logo-white.png"
      }
    },
    "datePublished": post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://mindvista.io/blog/${post.slug}`
    }
  }

  return (
    <div className="bg-[#0a0a0a]">
      <SEO 
        title={`${post.title} | MindVista Blog`}
        description={post.excerpt}
      />
      
      {/* Inject JSON-LD Schema specifically for the article */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[rgb(249,193,37)]/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container-main relative z-10 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#888888] hover:text-[rgb(249,193,37)] mb-8 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Blog
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="rounded-full bg-[rgb(249,193,37)]/10 text-[rgb(249,193,37)] px-3 py-1 text-xs font-bold border border-[rgb(249,193,37)]/20">
              {category?.label || post.category}
            </span>
            <span className="text-sm text-[#777777]">{post.readTime}</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 border-t border-white/10 pt-6">
            <div className="h-12 w-12 rounded-full bg-[#222] border border-white/10 flex items-center justify-center text-white font-bold text-lg">
              {post.author.charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-white">{post.author}</p>
              <p className="text-sm text-[#777777]">
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="container-main max-w-5xl mb-16">
        <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[21/9] bg-[#111]">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Article Content */}
      <section className="container-main max-w-3xl pb-20">
        <article className="prose prose-invert prose-lg max-w-none 
          prose-headings:text-white prose-headings:font-bold prose-h2:text-3xl prose-h3:text-2xl
          prose-p:text-[#bbbbbb] prose-p:leading-relaxed
          prose-a:text-[rgb(249,193,37)] prose-a:no-underline hover:prose-a:underline
          prose-strong:text-white
          prose-ul:text-[#bbbbbb] prose-li:marker:text-[rgb(249,193,37)]
          prose-blockquote:border-l-[rgb(249,193,37)] prose-blockquote:bg-[#151515] prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
          prose-code:text-[rgb(249,193,37)] prose-code:bg-[#1a1a1a] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
        ">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </article>

        {/* Lead Magnet / CTA Box */}
        <div className="mt-20 rounded-3xl border border-[rgb(249,193,37)]/30 bg-gradient-to-br from-[#151515] to-[#101010] p-8 md:p-12 text-center relative overflow-hidden">
           <div className="absolute top-0 right-0 w-32 h-32 bg-[rgb(249,193,37)]/10 rounded-bl-full"></div>
           <div className="relative z-10">
             <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to elevate your tech stack?</h3>
             <p className="text-[#aaaaaa] mb-8 max-w-xl mx-auto">
               Get a free technical consultation with our engineering team to discuss your next big project or review your current architecture.
             </p>
             <Link to="/contact" className="btn-primary inline-flex">
               Book a Free Consultation
             </Link>
           </div>
        </div>
      </section>
    </div>
  )
}

export default BlogPost
